import { X } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  /** Bloqueia fechar (Esc, clique fora, X) enquanto salva. */
  busy?: boolean;
  className?: string;
};

/** Janela de formulário (criar/editar). No celular ocupa a largura toda, com rolagem interna. */
export function Dialog({ open, onOpenChange, title, description, children, busy, className }: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-(--z-index-overlay) bg-overlay motion-safe:data-[state=open]:animate-[fade-in_150ms_var(--ease-out)]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-(--z-index-modal) flex max-h-[min(90dvh,48rem)] w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col rounded-lg border border-border bg-surface-raised shadow-md outline-none motion-safe:data-[state=open]:animate-[rise-in_200ms_var(--ease-out)]',
            className,
          )}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div>
              <DialogPrimitive.Title className="text-lg font-bold">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="mt-1 text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Fechar" disabled={busy}>
                <X aria-hidden />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <div className="overflow-y-auto px-5 py-4">{children}</div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
